import { Service } from '@angular/core';
import { map, Observable } from 'rxjs';
import { HttpParams } from '@angular/common/http';
import { KalaApiResponse } from '../models/kala-api.model';
import {
	WalletEventResponse,
	WalletSummaryResponse,
	WalletTransferCommand,
	WalletTransferRequestResponse
} from '../models/wallet-response.model';
import { BaseService } from './base.service';

/**
 * Reads all of the customer's wallet balances from the summary endpoint.
 *
 * The summary endpoint returns a bare array; balance values may be numeric or
 * money objects containing a `parsedValue`.
 */
@Service()
export class WalletService extends BaseService {
	/** @returns each wallet's balance, block, and available balance in ریال. */
	getWalletSummary(): Observable<WalletSummaryResponse[]> {
		return this.http.get<unknown>(`${this.walletApiPath}/wallet/summary`).pipe(
			map(response => {
				// Accept the previous envelope during API rollout, while the current contract is a bare array.
				if (!Array.isArray(response) && isWalletSummaryEnvelope(response)) {
					if (!response.isSuccessful) {
						throw new Error(response.error?.message ?? 'دریافت مانده کیف پول انجام نشد');
					}
					response = response.data;
				}

				if (!Array.isArray(response) || !response.every(isWalletSummaryResponse)) {
					throw new Error('پاسخ مانده کیف پول معتبر نیست');
				}

				return response.map(normalizeWalletSummaryResponse);
			})
		);
	}

	/** Opens a gateway top-up and returns the URL to which the app should redirect. */
	chargeWallet(command: {
		amount: number;
		callbackUrl: string;
		destination: WalletTransferCommand['to'];
	}): Observable<{ paymentUrl: string }> {
		return this.http.post<{ paymentUrl: string }>(`${this.walletApiPath}/wallet/charge`, command);
	}

	/** Records a transfer between two customer wallets. */
	transferWallet(command: WalletTransferCommand): Observable<void> {
		return this.http.post<void>(`${this.walletApiPath}/wallet/transfer`, command);
	}

	/**
	 * Reads the events recorded against one request — one per step it has taken.
	 *
	 * The endpoint answers the array bare rather than inside the usual envelope, so an
	 * envelope-shaped answer is read for its data and anything else comes back empty: a
	 * request with no events falls back to the state it reports itself.
	 */
	getEvents(id: string): Observable<WalletEventResponse[]> {
		const params = new HttpParams().set('id', id);
		return this.http.get<unknown>(`${this.walletApiPath}/Events`, { params }).pipe(
			map(response => {
				if (Array.isArray(response)) {
					return response as WalletEventResponse[];
				}

				const envelope = response as KalaApiResponse<WalletEventResponse[]>;
				return envelope.isSuccessful ? (envelope.data ?? []) : [];
			})
		);
	}

	/** Reads the current user's wallet transfer requests. */
	getTransferRequests(pageNumber = 1, pageSize = 20): Observable<WalletTransferRequestResponse[]> {
		const params = new HttpParams().set('pageNumber', pageNumber).set('pageSize', pageSize);
		return this.http
			.get<unknown>(`${this.walletApiPath}/wallet/transfer-requests`, { params })
			.pipe(map(unwrapTransferRequests));
	}

	/**
	 * «تراکنش‌های اخیر» on the wallet tab: today's requests, however they ended, and every
	 * request still on its way whatever day it was registered on. The server decides which.
	 *
	 * TODO: the endpoint is agreed but not yet live on the backend.
	 */
	getLatestActiveTransferRequests(): Observable<WalletTransferRequestResponse[]> {
		return this.http
			.get<unknown>(`${this.walletApiPath}/wallet/transfer-requests/latest-active`)
			.pipe(map(unwrapTransferRequests));
	}
}

/** The transfer-request endpoints answer with a bare list or inside the usual envelope. */
function unwrapTransferRequests(response: unknown): WalletTransferRequestResponse[] {
	if (Array.isArray(response)) {
		return response as WalletTransferRequestResponse[];
	}

	const envelope = response as KalaApiResponse<WalletTransferRequestResponse[]>;
	if (!envelope.isSuccessful) {
		throw new Error(envelope.error?.message ?? 'دریافت تراکنش‌های کیف‌پول انجام نشد');
	}

	return envelope.data ?? [];
}

function isWalletSummaryResponse(value: unknown): value is WalletSummaryResponse {
	if (typeof value !== 'object' || value === null) {
		return false;
	}

	const wallet = value as Partial<WalletSummaryResponse>;
	return (
		typeof wallet.id === 'number' &&
		Number.isFinite(wallet.id) &&
		(wallet.walletTypeName === null || typeof wallet.walletTypeName === 'string') &&
		isWalletApplicationName(wallet.application) &&
		isWalletBalance(wallet.balance) &&
		isNullableFiniteNumber(wallet.block) &&
		isNullableFiniteNumber(wallet.availableBalance)
	);
}

function isWalletSummaryEnvelope(value: unknown): value is KalaApiResponse<unknown> {
	return typeof value === 'object' && value !== null && 'isSuccessful' in value;
}

function isWalletBalance(value: unknown): boolean {
	if (typeof value === 'number') {
		return Number.isFinite(value);
	}

	if (typeof value !== 'object' || value === null || !('parsedValue' in value)) {
		return false;
	}

	const parsedValue = (value as { parsedValue: unknown }).parsedValue;
	return typeof parsedValue === 'number' && Number.isFinite(parsedValue);
}

function normalizeWalletSummaryResponse(wallet: WalletSummaryResponse): WalletSummaryResponse {
	const balance = wallet.balance as number | { parsedValue: number };
	return { ...wallet, balance: typeof balance === 'number' ? balance : balance.parsedValue };
}

function isWalletApplicationName(value: unknown): value is WalletSummaryResponse['application'] {
	return (
		value === 'None' ||
		value === 'Main' ||
		value === 'CDC' ||
		value === 'WareHouse' ||
		value === 'Future' ||
		value === 'Option'
	);
}

function isNullableFiniteNumber(value: unknown): value is number | null {
	return value === null || (typeof value === 'number' && Number.isFinite(value));
}
